export function frameAmounts(variants, options, frame = options?.[1]) {
  if (!Array.isArray(options) || options.length !== 3) return null;
  const matches = variants.filter(v => v.options?.[0] === options[0] && v.options?.[2] === options[2]
    && /^[PC][^-]+-[A-Za-z0-9]{2}-[^-]+$/.test(v.sku || ''));
  const plain = matches.find(v => v.sku.split('-')[1] === '00');
  const chosen = matches.find(v => v.options[1] === frame);
  if (!plain || !chosen || chosen.sku.split('-')[0] !== plain.sku.split('-')[0]
    || chosen.sku.split('-')[2] !== plain.sku.split('-')[2]
    || !Number.isInteger(plain.price) || !Number.isInteger(chosen.price) || chosen.price < plain.price) return null;
  const baseReference = Math.max(plain.price, Number(plain.compare_at_price) || 0);
  const totalReference = Math.max(chosen.price, Number(chosen.compare_at_price) || 0);
  return {base: plain.price, baseReference, extra: chosen.price - plain.price,
    extraReference: totalReference - baseReference, total: chosen.price, totalReference,
    framed: chosen.id !== plain.id, canvas: chosen.sku.startsWith('C')};
}

export function discountBadges(amounts) {
  const percent = (price, reference) => {
    if (!Number.isInteger(price) || !Number.isInteger(reference) || reference <= price || reference <= 0) return null;
    return Math.round((reference - price) * 100 / reference) || null;
  };
  return {
    base: !amounts.canvas ? percent(amounts.base, amounts.baseReference) : null,
    frame: !amounts.canvas && amounts.framed ? percent(amounts.extra, amounts.extraReference) : null,
    total: amounts.canvas ? percent(amounts.total, amounts.totalReference) : null,
  };
}

export function hasPriceBreakdown(amounts) {
  return Boolean(amounts && amounts.framed && !amounts.canvas);
}

class ArtestaFramePrices extends (globalThis.HTMLElement || class {}) {
  connectedCallback() {
    this.picker = this.closest('variant-picker');
    if (!this.picker) return;
    this.variants = JSON.parse(this.querySelector('[data-frame-variants]').textContent);
    this.format = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency', currency: this.dataset.currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    });
    this.onChange = () => queueMicrotask(() => this.isConnected && this.update());
    this.picker.addEventListener('change', this.onChange);
    this.update();
  }
  disconnectedCallback() {
    this.picker?.removeEventListener('change', this.onChange);
  }
  pair(root, currentKey, oldKey, price, reference, prefix = '') {
    root.querySelector(currentKey).textContent = prefix + this.format.format(price / 100);
    const previous = root.querySelector(oldKey);
    previous.hidden = !(reference > price);
    previous.textContent = reference > price ? this.format.format(reference / 100) : '';
  }
  update() {
    const options = [0, 1, 2].map(i => this.picker.querySelector(`fieldset[data-fieldset-index="${i}"] input:checked`)?.value);
    const amounts = frameAmounts(this.variants, options);
    const showBreakdown = hasPriceBreakdown(amounts);
    this.querySelector('[data-price-detail]').hidden = !showBreakdown;
    if (!showBreakdown) return;
    this.querySelector('[data-base-label]').textContent = `${options[0]} · ${options[2]}`;
    this.querySelector('[data-frame-label]').textContent = options[1];
    this.querySelector('[data-base-row]').hidden = amounts.canvas;
    this.querySelector('[data-frame-row]').hidden = amounts.canvas || !amounts.framed;
    const totalLabel = this.querySelector('[data-total-label]');
    totalLabel.textContent = amounts.canvas ? `${options[0]} · ${options[2]}${amounts.framed ? ' · ' + options[1] : ''}` : totalLabel.dataset.defaultLabel;
    const badges = discountBadges(amounts);
    for (const [name, value] of Object.entries(badges)) {
      const badge = this.querySelector(`[data-discount-${name}]`);
      badge.hidden = value === null;
      badge.textContent = value === null ? '' : `-${value}%`;
    }
    this.pair(this, '[data-base-now]', '[data-base-before]', amounts.base, amounts.baseReference);
    this.pair(this, '[data-extra-now]', '[data-extra-before]', amounts.extra, amounts.extraReference, '+');
    this.pair(this, '[data-total-now]', '[data-total-before]', amounts.total, amounts.totalReference);
  }
}

if (globalThis.customElements && !customElements.get('artesta-frame-prices')) {
  customElements.define('artesta-frame-prices', ArtestaFramePrices);
}
